import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlaceIcon from "@mui/icons-material/Place";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { Fetching } from "components/common/loading/fetching";
import { useVerifyPeriods } from "components/manualReview/hooks/useVerifyPeriods";
import { dayjsSerializer } from "components/manualReview/utils/timeUtils";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { Serialiser } from "hooks/serialisers/types";
import { useTenantTranslation } from "hooks/formatters";
import { useActivityTimer } from "hooks/useActivityTimer";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useQueryParamState } from "hooks/useQueryParamState";
import { useTenant } from "hooks/settings";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";
import { TargetedReviewPeriodInspector } from "./TargetedReviewPeriodInspector";
import { TargetedReviewTable } from "./TargetedReviewTable";
import {
  canOpenRow,
  ConsumptionFilter,
  groupMatchesConsumptionFilter,
  groupRows,
  hasDataFromAfterSave,
  LastSave,
  mergeSessionRows,
  nextVisibleGroupToReview,
  pruneFrozenGroup,
  resolveFrozenGroupRows,
  reviewPeriodsByStart,
  sessionRowsToKeep,
  settledIdsAfterDispositions,
  TargetedReviewDisposition,
  TargetedReviewPeriodGroup,
  TargetedReviewPeriodRow,
  unverifiedAgainIds,
} from "./targetedReviewRows";
import { TargetedReviewDateStepper } from "./TargetedReviewDateStepper";
import { useTargetedReviewLatencySpans } from "./useTargetedReviewLatencySpans";
import { useTargetedReviewRows } from "./useTargetedReviewRows";
import { useTargetedReviewSelection } from "./useTargetedReviewSelection";

dayjs.extend(utc);

type VerifiedVisibility = "shown" | "hidden";

const verifiedVisibilitySerialiser: Serialiser<VerifiedVisibility, never> = {
  format: (value) => value,
  parse: (text) =>
    text === "shown" || text === "hidden"
      ? { valid: true, value: text }
      : { valid: false },
};

const withoutIds = (ids: Set<string>, exclude: Set<string>): Set<string> =>
  new Set([...ids].filter((id) => !exclude.has(id)));

const consumptionFilterSerialiser: Serialiser<ConsumptionFilter, never> = {
  format: (value) => value,
  parse: (text) =>
    text === "all" || text === "consumption" || text === "non_consumption"
      ? { valid: true, value: text }
      : { valid: false },
};

type Grouping = "on" | "off";

const groupingSerialiser: Serialiser<Grouping, never> = {
  format: (value) => value,
  parse: (text) =>
    text === "on" || text === "off"
      ? { valid: true, value: text }
      : { valid: false },
};

export const TargetedReviewLocationPicker = () => {
  const { t } = useTenantTranslation();
  const { locationNames: locations, isLoading } = useLabellingLocations();

  return (
    <AccountPageLayout title={t("targetedReview")}>
      <Stack spacing={3} py={4}>
        <Typography variant="h5">{t("selectLocation")}</Typography>
        <Typography variant="body1" color="text.secondary">
          {t("targetedReviewSelectLocation")}
        </Typography>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : locations.length === 0 ? (
          <Alert severity="info">{t("noLocationsConfigured")}</Alert>
        ) : (
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {locations.map((location) => (
              <Card key={location} sx={{ minWidth: 200 }}>
                <CardActionArea component={Link} to={location} sx={{ p: 2 }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <PlaceIcon color="primary" sx={{ fontSize: 32 }} />
                      <Typography variant="h6">{location}</Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </AccountPageLayout>
  );
};

export const TargetedReviewPage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const navigate = useNavigate();
  const { location } = useParams<{ location: string }>();

  const [day, setDay] = useQueryParamState<Dayjs, never>(
    "date",
    dayjsSerializer,
    dayjs.utc().startOf("day")
  );

  const [verifiedVisibility, setVerifiedVisibility] = useQueryParamState<
    VerifiedVisibility,
    never
  >("verified", verifiedVisibilitySerialiser, "hidden");
  const includeVerified = verifiedVisibility === "shown";

  const [consumptionFilter, setConsumptionFilter] = useQueryParamState<
    ConsumptionFilter,
    never
  >("consumption", consumptionFilterSerialiser, "all");
  const { drop_areas: dropAreas } = useScrapEyeMetadata();
  const hasConsumptionData = dropAreas.length > 0;
  const effectiveConsumptionFilter: ConsumptionFilter = hasConsumptionData
    ? consumptionFilter
    : "all";

  const [grouping, setGrouping] = useQueryParamState<Grouping, never>(
    "grouping",
    groupingSerialiser,
    "on"
  );
  const groupingEnabled = grouping === "on";

  const {
    periodRows,
    labellerTimelines,
    heats,
    loadingWindows,
    dayReviewEvents,
    dayReviewPeriods,
    reviewSlotRevisions,
    railcarSlotMaterials,
    exclusions,
    isFetching,
    isError,
  } = useTargetedReviewRows({ location: location ?? "", day, includeVerified });

  const { getElapsedSeconds, reset, sessionStartedAt } = useActivityTimer({
    storageKey: location
      ? `targeted-review-timer-${tenantName}-${location}`
      : undefined,
  });

  const { verifyPeriods, isVerifying } = useVerifyPeriods({
    tenantName,
    location: location ?? "",
    getElapsedSeconds,
    resetTimer: reset,
    reviewSlotRevisions,
    sessionStartedAt,
  });

  const dayReviewEventsRef = useRef(dayReviewEvents);
  dayReviewEventsRef.current = dayReviewEvents;
  const dayReviewPeriodsRef = useRef(dayReviewPeriods);
  dayReviewPeriodsRef.current = dayReviewPeriods;
  const locationRef = useRef(location);
  locationRef.current = location;
  const dayRef = useRef(day);
  dayRef.current = day;

  const [lastSave, setLastSave] = useState<LastSave | null>(null);

  const dataIsFresh = hasDataFromAfterSave({
    lastSave,
    dayReviewEvents,
    dayReviewPeriods,
    isFetching,
  });

  const { trackSave } = useTargetedReviewLatencySpans({
    tenantName,
    location,
    day: day.format("YYYY-MM-DD"),
    isFetching,
    isError,
    rowCount: periodRows.length,
    hasFreshDataAfterSave: dataIsFresh,
  });

  const guardedVerifyPeriods = useCallback<typeof verifyPeriods>(
    (...args) =>
      trackSave(async () => {
        const savingLocation = locationRef.current;
        const savingDay = dayRef.current;
        await verifyPeriods(...args);
        if (
          locationRef.current !== savingLocation ||
          dayRef.current !== savingDay
        ) {
          return;
        }
        setLastSave({
          dayReviewEvents: dayReviewEventsRef.current,
          dayReviewPeriods: dayReviewPeriodsRef.current,
        });
      }),
    [verifyPeriods, trackSave]
  );

  const [reviewedIds, setReviewedIds] = useState<Set<string>>(() => new Set());
  const [broughtInIds, setBroughtInIds] = useState<Set<string>>(
    () => new Set()
  );
  const [seenIds, setSeenIds] = useState<Set<string>>(() => new Set());
  const [verifiedRowsById, setVerifiedRowsById] = useState<
    Map<string, TargetedReviewPeriodRow>
  >(() => new Map());

  const liveReviewPeriodByStart = useMemo(
    () => reviewPeriodsByStart(dayReviewPeriods),
    [dayReviewPeriods]
  );

  const liveRowById = useMemo(
    () => new Map(periodRows.map((row) => [row.id, row])),
    [periodRows]
  );

  const displayRows = useMemo(
    () =>
      mergeSessionRows({
        sessionRows: verifiedRowsById,
        liveReviewPeriodByStart,
        liveRowById,
      }),
    [verifiedRowsById, liveReviewPeriodByStart, liveRowById]
  );

  const displayRowById = useMemo(
    () => new Map(displayRows.map((row) => [row.id, row])),
    [displayRows]
  );
  const displayRowIds = useMemo(
    () => new Set(displayRows.map((row) => row.id)),
    [displayRows]
  );

  const settledThisSession = useMemo(
    () => new Set([...reviewedIds, ...broughtInIds]),
    [reviewedIds, broughtInIds]
  );

  const unverifiedAgain = useMemo(
    () =>
      unverifiedAgainIds({
        settledIds: settledThisSession,
        liveReviewPeriodByStart,
        hasFreshData: dataIsFresh,
      }),
    [settledThisSession, liveReviewPeriodByStart, dataIsFresh]
  );

  const lastFreshUnverifiedAgainRef = useRef<Set<string>>(new Set());
  if (dataIsFresh) {
    lastFreshUnverifiedAgainRef.current = unverifiedAgain;
  }
  const groupingUnverifiedAgain = dataIsFresh
    ? unverifiedAgain
    : lastFreshUnverifiedAgainRef.current;

  const effectiveReviewedIds = useMemo(
    () => withoutIds(reviewedIds, unverifiedAgain),
    [reviewedIds, unverifiedAgain]
  );
  const effectiveBroughtInIds = useMemo(
    () => withoutIds(broughtInIds, unverifiedAgain),
    [broughtInIds, unverifiedAgain]
  );
  const effectiveSeenIds = useMemo(
    () => withoutIds(seenIds, unverifiedAgain),
    [seenIds, unverifiedAgain]
  );

  const settledIds = useMemo(
    () => new Set([...effectiveReviewedIds, ...effectiveBroughtInIds]),
    [effectiveReviewedIds, effectiveBroughtInIds]
  );

  const rowCanOpen = useCallback(
    (row: TargetedReviewPeriodRow) =>
      canOpenRow({
        row,
        settledIds,
        liveRowById,
        liveReviewPeriodByStart,
        hasFreshData: dataIsFresh,
      }),
    [settledIds, liveRowById, liveReviewPeriodByStart, dataIsFresh]
  );

  const [frozenGroup, setFrozenGroup] = useTargetedReviewSelection({
    location,
    day,
    canOpen: (group) => {
      const rows = resolveFrozenGroupRows({
        frozen: group,
        rowById: displayRowById,
      });
      return rows.length === group.rowIds.length && rows.every(rowCanOpen);
    },
  });

  useEffect(() => {
    if (!frozenGroup) return;
    const pruned = pruneFrozenGroup({
      frozen: frozenGroup,
      rowById: displayRowById,
    });
    if (pruned !== frozenGroup) {
      setFrozenGroup({ group: pruned, history: "replace" });
    }
  }, [frozenGroup, displayRowById, setFrozenGroup]);

  const groupingSettledIds = useMemo(
    () =>
      new Set([
        ...withoutIds(reviewedIds, groupingUnverifiedAgain),
        ...withoutIds(broughtInIds, groupingUnverifiedAgain),
      ]),
    [reviewedIds, broughtInIds, groupingUnverifiedAgain]
  );

  const allGroups = useMemo(
    () =>
      groupRows({
        rows: displayRows,
        settledIds: groupingSettledIds,
        grouping: groupingEnabled,
      }),
    [displayRows, groupingSettledIds, groupingEnabled]
  );

  const visibleGroups = useMemo(
    () =>
      allGroups.filter((group) =>
        groupMatchesConsumptionFilter(group, effectiveConsumptionFilter)
      ),
    [allGroups, effectiveConsumptionFilter]
  );

  // Row ids are just start.toISOString(), with no location qualifier — two locations
  // sharing the same UTC grid produce identical ids, so this resets on a location or
  // day change to avoid conflating one location's dispositions with another's, not
  // just staleness.
  useEffect(() => {
    /* eslint-disable @eslint-react/set-state-in-effect -- resetting session state on a genuine context change, not deriving render output */
    setReviewedIds(new Set());
    setBroughtInIds(new Set());
    setSeenIds(new Set());
    setVerifiedRowsById(new Map());
    setLastSave(null);
    /* eslint-enable @eslint-react/set-state-in-effect */
  }, [location, day]);

  const handleGroupClick = useCallback(
    (group: TargetedReviewPeriodGroup) => {
      if (!group.rows.every(rowCanOpen)) return;
      setFrozenGroup({
        group: { rowIds: group.rowIds, start: group.start, end: group.end },
        history: "push",
      });
    },
    [rowCanOpen, setFrozenGroup]
  );

  const handleClose = useCallback(() => {
    setFrozenGroup({ group: undefined, history: "push" });
  }, [setFrozenGroup]);

  const handleDispositioned = useCallback(
    (dispositions: TargetedReviewDisposition[]) => {
      const validDispositions = dispositions.filter((disposition) =>
        displayRowIds.has(disposition.periodId)
      );
      const periodIds = validDispositions.map(
        (disposition) => disposition.periodId
      );
      const nextSeen = new Set([...seenIds, ...periodIds]);
      setSeenIds(nextSeen);

      // "verified" and "broughtIn" both leave the row settled (not asked about
      // again) and both retain the row across a refetch; only the table's own
      // disposition tag distinguishes them.
      const settledThisCall = validDispositions
        .filter((disposition) => disposition.outcome !== "skipped")
        .map((disposition) => disposition.periodId);
      if (settledThisCall.length > 0) {
        setReviewedIds(
          (previous) => new Set([...previous, ...settledThisCall])
        );
        const settledRows = sessionRowsToKeep({
          displayRows,
          settledIds: settledThisCall,
        });
        if (settledRows.length > 0) {
          setVerifiedRowsById(
            (previous) =>
              new Map([
                ...previous,
                ...settledRows.map((row) => [row.id, row] as const),
              ])
          );
        }
      }

      const broughtInThisCall = validDispositions
        .filter((disposition) => disposition.outcome === "broughtIn")
        .map((disposition) => disposition.periodId);
      if (broughtInThisCall.length > 0) {
        setBroughtInIds(
          (previous) => new Set([...previous, ...broughtInThisCall])
        );
      }

      // A period tagged "broughtIn" earlier this session can later be stepped through
      // properly and verified as its own seed row — that must supersede the stale tag,
      // not sit alongside it.
      const verifiedThisCall = validDispositions
        .filter((disposition) => disposition.outcome === "verified")
        .map((disposition) => disposition.periodId);
      if (verifiedThisCall.length > 0) {
        setBroughtInIds((previous) => {
          const next = new Set(previous);
          verifiedThisCall.forEach((id) => next.delete(id));
          return next;
        });
      }

      if (
        !frozenGroup ||
        locationRef.current !== location ||
        !dayRef.current.isSame(day)
      ) {
        return;
      }
      const correctedNextSeen = withoutIds(
        nextSeen,
        withoutIds(unverifiedAgain, new Set(periodIds))
      );
      const correctedGroupingSettledIds = settledIdsAfterDispositions({
        settledIds: groupingSettledIds,
        dispositions: validDispositions,
      });
      const next = nextVisibleGroupToReview({
        rows: displayRows,
        settledIds: correctedGroupingSettledIds,
        grouping: groupingEnabled,
        consumptionFilter: effectiveConsumptionFilter,
        after: frozenGroup.end,
        seenIds: correctedNextSeen,
      });
      setFrozenGroup({
        group: next
          ? { rowIds: next.rowIds, start: next.start, end: next.end }
          : undefined,
        history: "replace",
      });
    },
    [
      seenIds,
      displayRows,
      displayRowIds,
      groupingSettledIds,
      groupingEnabled,
      effectiveConsumptionFilter,
      unverifiedAgain,
      frozenGroup,
      setFrozenGroup,
      location,
      day,
    ]
  );

  const expandedGroupRows = frozenGroup
    ? resolveFrozenGroupRows({ frozen: frozenGroup, rowById: displayRowById })
    : [];
  const [firstExpandedGroupRow] = expandedGroupRows;
  const expandedGroup: TargetedReviewPeriodGroup | undefined =
    frozenGroup && firstExpandedGroupRow
      ? {
          id: `group:${firstExpandedGroupRow.id}`,
          start: frozenGroup.start,
          end: frozenGroup.end,
          rowIds: expandedGroupRows.map((row) => row.id),
          rows: expandedGroupRows,
        }
      : undefined;

  if (!location) {
    return <TargetedReviewLocationPicker />;
  }

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: expandedGroup ? "none" : "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          gap: 3,
          p: 3,
          overflow: "auto",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <IconButton
            onClick={() => void navigate(`/${tenantName}/tool/targeted-review`)}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ flex: 1 }}>
            {t("targetedReview")} - {location}
          </Typography>
          <TargetedReviewDateStepper
            location={location}
            selectedDate={day}
            setSelectedDate={(next) => {
              setDay(dayjs.utc(next.format("YYYY-MM-DD")).startOf("day"));
            }}
          />
          {hasConsumptionData ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={consumptionFilter}
              onChange={(_, value: ConsumptionFilter | null) => {
                if (value !== null) setConsumptionFilter(value);
              }}
            >
              <ToggleButton value="all">{t("all")}</ToggleButton>
              <ToggleButton value="consumption">
                {t("consumption")}
              </ToggleButton>
              <ToggleButton value="non_consumption">
                {t("nonConsumption")}
              </ToggleButton>
            </ToggleButtonGroup>
          ) : null}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeVerified}
                onChange={(event) => {
                  setVerifiedVisibility(
                    event.target.checked ? "shown" : "hidden"
                  );
                }}
              />
            }
            label={t("includeVerified")}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={groupingEnabled}
                onChange={(event) => {
                  setGrouping(event.target.checked ? "on" : "off");
                }}
              />
            }
            label={t("groupPeriods")}
          />
        </Stack>

        {isError ? (
          <Alert severity="error">{t("errorLoadingData")}</Alert>
        ) : (
          <Fetching
            fetching={isFetching}
            sx={{
              minHeight: 200,
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {visibleGroups.length === 0 ? (
              isFetching ? null : (
                <Alert severity="info">
                  {t(
                    displayRows.length === 0
                      ? "noLikelyErrorsInRange"
                      : "noPeriodsMatchFilter"
                  )}
                </Alert>
              )
            ) : (
              // The grid needs a bounded height to virtualise its rows, so it
              // takes the remaining space and scrolls inside itself.
              <Box
                sx={{
                  minHeight: 400,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TargetedReviewTable
                  key={`${location}-${day.toISOString()}`}
                  groups={visibleGroups}
                  reviewedIds={effectiveReviewedIds}
                  broughtInIds={effectiveBroughtInIds}
                  seenIds={effectiveSeenIds}
                  onGroupClick={handleGroupClick}
                />
              </Box>
            )}
          </Fetching>
        )}
      </Box>

      {expandedGroup ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <TargetedReviewPeriodInspector
            key={expandedGroup.start.toISOString()}
            group={expandedGroup}
            location={location}
            labellerTimelines={labellerTimelines}
            heats={heats}
            loadingWindows={loadingWindows}
            dayReviewEvents={dayReviewEvents}
            reviewSlotRevisions={reviewSlotRevisions}
            dayReviewPeriods={dayReviewPeriods}
            railcarSlotMaterials={railcarSlotMaterials}
            exclusions={exclusions}
            verifyPeriods={guardedVerifyPeriods}
            isVerifying={isVerifying}
            isEventsFetching={isFetching}
            onDispositioned={handleDispositioned}
            onClose={handleClose}
          />
        </Box>
      ) : null}
    </Stack>
  );
};
